import { useState } from "react";

import { useDialogTriggers, useDialogStates } from "../../Dialogs";

import { useCreateDefaultWorkflow } from "./useCreateDefaultWorkflow";
import useCreateDefaultSkillCollection from "./useCreateDefaultSkillCollection";
import useCreateDefaultTool from "./useCreateDefaultTool";
import useCreateDefaultPrompt from "./useCreateDefaultPrompt";
import useCreateDefaultInputVariableSet from "./useCreateDefaultInputVariableSet";

import { SmartTableCreateDialogProps } from "@/components/SmartTableCreateDialog";
import { EntityType } from "@/queries/folder-entities/types";

const useNewItemActions = (
  currentFolderId?: number,
  openInNewTab = false,
  _workspaceId?: number | null,
) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<EntityType | null>(null);
  const [isSmartTableCreateDialogOpen, setIsSmartTableCreateDialogOpen] = useState(false);

  const { createFolder, selectReleaseLabel } = useDialogTriggers();

  const { isSelectReleaseLabelLoading } = useDialogStates();

  const { create: createWorkflow } = useCreateDefaultWorkflow(currentFolderId, openInNewTab);

  const { create: createDefaultPrompt } = useCreateDefaultPrompt(
    currentFolderId,
    false,
    openInNewTab,
  );

  const { create: createDefaultSnippet } = useCreateDefaultPrompt(
    currentFolderId,
    true,
    openInNewTab,
  );

  const { create: createDefaultInputVariableSet } = useCreateDefaultInputVariableSet(
    currentFolderId,
    openInNewTab,
  );
  const { create: createDefaultSkillCollection } = useCreateDefaultSkillCollection(
    currentFolderId ?? null,
    openInNewTab,
  );

  const { create: createDefaultTool } = useCreateDefaultTool(currentFolderId ?? null, openInNewTab);

  const runCreateAction = async (type: EntityType, action: () => Promise<void>) => {
    setInternalLoading(true);
    setLoadingType(type);
    try {
      await action();
    } finally {
      setInternalLoading(false);
      setLoadingType(null);
    }
  };

  const smartTableCreateDialogProps: SmartTableCreateDialogProps = {
    open: isSmartTableCreateDialogOpen,
    onOpenChange: setIsSmartTableCreateDialogOpen,
    folderId: currentFolderId ?? null,
    openInNewTab,
  };

  const actions: Partial<Record<EntityType, () => Promise<void>>> = {
    [EntityType.FOLDER]: async () => {
      createFolder(currentFolderId || null, !openInNewTab);
    },

    [EntityType.WORKFLOW]: async () => {
      await runCreateAction(EntityType.WORKFLOW, createWorkflow);
    },

    [EntityType.PROMPT]: async () => {
      await runCreateAction(EntityType.PROMPT, createDefaultPrompt);
    },

    [EntityType.AB_TEST]: async () => {
      selectReleaseLabel();
    },

    [EntityType.SNIPPET]: async () => {
      await runCreateAction(EntityType.SNIPPET, createDefaultSnippet);
    },

    [EntityType.INPUT_VARIABLE_SET]: async () => {
      await runCreateAction(EntityType.INPUT_VARIABLE_SET, createDefaultInputVariableSet);
    },

    [EntityType.SKILL_COLLECTION]: async () => {
      await runCreateAction(EntityType.SKILL_COLLECTION, createDefaultSkillCollection);
    },

    [EntityType.SMART_TABLE]: async () => {
      setIsSmartTableCreateDialogOpen(true);
    },

    [EntityType.TOOL]: async () => {
      await runCreateAction(EntityType.TOOL, createDefaultTool);
    },
  };

  // Combine internal loading and dialog loading states
  const isLoading = internalLoading || isSelectReleaseLabelLoading;

  return {
    actions,
    isLoading,
    loadingType,
    smartTableCreateDialogProps,
  };
};

export default useNewItemActions;
